import { useState, useEffect, useRef, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, AlertCircle, Play } from 'lucide-react';
import { useProducts } from '../contexts/ProductContext';
import { useFonts } from '../contexts/FontContext';
import type { Product, CustomizationField, ProductVariation, VariationImage, FieldFont, CartItem } from '@jigflow/shared';

interface ProductModalProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onProcess: (item: CartItem) => void;
}

interface DisplayField {
  id: string;
  label: string;
  type: string;
  options?: string[];
  maxLength?: number;
  placeholder?: string;
  fontFamily?: string;
  score?: boolean;
  engrave?: boolean;
  isVariationSelector?: boolean;
}

export default function ProductModal({ product, isOpen, onClose, onProcess }: ProductModalProps) {
  const { getFieldsByProductId, getVariationsByProductId, getVariationImages, getTemplateByProductId, getShapesByTemplateId } = useProducts();
  const { getFieldFonts } = useFonts();

  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [fieldFonts, setFieldFonts] = useState<Record<string, string>>({});
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [images, setImages] = useState<string[]>([]);
  const [fields, setFields] = useState<DisplayField[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [availableFonts, setAvailableFonts] = useState<Record<string, FieldFont[]>>({});
  const [variations, setVariations] = useState<ProductVariation[]>([]);
  const [usedFieldIds, setUsedFieldIds] = useState<Set<string> | null>(null);
  const defaultImagesRef = useRef<string[]>([]);

  useEffect(() => {
    if (product && isOpen) {
      setFormValues({});
      setFieldFonts({});
      setAvailableFonts({});
      setActiveImageIndex(0);
      setVariations([]);
      setUsedFieldIds(null);

      const allImages: string[] = [];
      if (product.image) allImages.push(product.image);
      if (product.images && product.images.length > 0) {
        const additional = product.images.filter(img => img !== product.image);
        allImages.push(...additional);
      }
      const finalImages = allImages.length > 0 ? allImages : ['https://placehold.co/300x300?text=No+Image'];
      defaultImagesRef.current = finalImages;
      setImages(finalImages);

      loadFields(product.id);
      loadVariations(product.id);
    }
  }, [product, isOpen]);

  const loadVariations = async (productId: string) => {
    try {
      const vars = await getVariationsByProductId(productId);
      setVariations(vars);
    } catch (error) {
      console.error('[ProductModal] Failed to load variations:', error);
    }
  };

  // Watch for variation selection changes and update images
  useEffect(() => {
    const updateImagesForVariation = async () => {
      if (!product || variations.length === 0) return;
      const variationField = fields.find(f => f.isVariationSelector);
      if (!variationField) return;
      const selectedVariationName = formValues[variationField.id];
      if (!selectedVariationName) {
        setImages(defaultImagesRef.current);
        setActiveImageIndex(0);
        return;
      }
      const selectedVariation = variations.find(v => v.name === selectedVariationName);
      if (!selectedVariation) {
        setImages(defaultImagesRef.current);
        setActiveImageIndex(0);
        return;
      }
      try {
        const varImages = await getVariationImages(selectedVariation.id);
        if (varImages.length > 0) {
          const sorted = [...varImages].sort((a, b) => {
            if (a.isPrimary && !b.isPrimary) return -1;
            if (!a.isPrimary && b.isPrimary) return 1;
            return a.orderIndex - b.orderIndex;
          });
          setImages(sorted.map(img => img.image));
          setActiveImageIndex(0);
        } else {
          setImages(defaultImagesRef.current);
          setActiveImageIndex(0);
        }
      } catch (error) {
        console.error('[ProductModal] Failed to load variation images:', error);
        setImages(defaultImagesRef.current);
        setActiveImageIndex(0);
      }
    };
    updateImagesForVariation();
  }, [formValues, fields, variations, product, getVariationImages]);

  // Load template and filter fields
  useEffect(() => {
    const loadUsedFields = async () => {
      if (!product) return;
      if (variations.length === 0) {
        setUsedFieldIds(null);
        return;
      }
      const variationField = fields.find(f => f.isVariationSelector);
      const selectedValue = variationField ? formValues[variationField.id] : null;
      let variationId: string | undefined = undefined;
      if (selectedValue && selectedValue !== 'Default') {
        const variation = variations.find(v => v.name === selectedValue);
        variationId = variation?.id;
      }
      try {
        const template = await getTemplateByProductId(product.id, variationId);
        if (!template) {
          setUsedFieldIds(new Set());
          return;
        }
        const shapes = await getShapesByTemplateId(template.id);
        const fieldIds = new Set(shapes.map(s => s.fieldId).filter(Boolean) as string[]);
        setUsedFieldIds(fieldIds);
      } catch (error) {
        console.error('[ProductModal] Failed to load template fields:', error);
        setUsedFieldIds(null);
      }
    };
    loadUsedFields();
  }, [formValues, fields, variations, product, getTemplateByProductId, getShapesByTemplateId]);

  const loadFields = async (productId: string) => {
    setLoadingFields(true);
    try {
      const dbFields = await getFieldsByProductId(productId);
      const convertedFields: DisplayField[] = dbFields.map((f: CustomizationField) => {
        let parsedOptions: string[] | undefined;
        if (f.options) {
          try {
            parsedOptions = typeof f.options === 'string' ? JSON.parse(f.options as string) : f.options;
          } catch {
            parsedOptions = undefined;
          }
        }
        const isVariationSelector = f.label === '__variation_selector__';
        return {
          id: f.id,
          label: isVariationSelector ? 'Select Variation' : f.label,
          type: f.type === 'checkbox' ? 'text' : f.type,
          options: parsedOptions,
          maxLength: f.maxLength,
          placeholder: f.type === 'text' ? `Enter ${f.label.toLowerCase()}` : undefined,
          score: f.score ?? false,
          engrave: f.engrave ?? true,
          fontFamily: f.fontFamily || 'Roboto',
          isVariationSelector,
        };
      });
      setFields(convertedFields);

      // Load available fonts for each text field
      const initialFonts: Record<string, string> = {};
      const fontsByField: Record<string, FieldFont[]> = {};
      for (const field of convertedFields) {
        if (field.type === 'text') {
          try {
            const fonts = await getFieldFonts(field.id);
            if (fonts.length > 0) {
              fontsByField[field.id] = fonts;
              const primary = fonts.find(f => f.isPrimary);
              initialFonts[field.id] = primary ? primary.family : fonts[0].family;
            } else {
              initialFonts[field.id] = field.fontFamily || 'Roboto';
            }
          } catch {
            initialFonts[field.id] = field.fontFamily || 'Roboto';
          }
        }
      }
      setAvailableFonts(fontsByField);
      setFieldFonts(initialFonts);
    } catch (error) {
      console.error('[ProductModal] Failed to load fields:', error);
      setFields(product?.fields?.map(f => ({ ...f, type: f.type as string })) || []);
    } finally {
      setLoadingFields(false);
    }
  };

  const displayedFields = useMemo(() => {
    if (usedFieldIds === null) return fields;
    return fields.filter(f => {
      if (f.isVariationSelector) return true;
      return usedFieldIds.has(f.id);
    });
  }, [fields, usedFieldIds]);

  if (!isOpen || !product) return null;

  const handleInputChange = (fieldId: string, value: string) => {
    setFormValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleFontChange = (fieldId: string, fontFamily: string) => {
    setFieldFonts(prev => ({ ...prev, [fieldId]: fontFamily }));
  };

  const handleCheckboxChange = (fieldId: string, checked: boolean) => {
    setFormValues(prev => ({ ...prev, [fieldId]: checked ? 'Yes' : '' }));
  };

  const variationField = fields.find(f => f.isVariationSelector);
  const hasVariations = variationField && variationField.options && variationField.options.length > 0;
  const isVariationSelected = !hasVariations || (formValues[variationField?.id || ''] && formValues[variationField?.id || ''] !== '');

  const handleAction = () => {
    if (!isVariationSelected) return;
    const item: CartItem = {
      id: `process-${Date.now()}`,
      productId: product.id,
      productName: product.name,
      productImage: product.image || '',
      price: product.price,
      customizations: formValues,
      fontSelections: fieldFonts,
    };
    onProcess(item);
  };

  const nextImage = () => setActiveImageIndex(prev => (prev + 1) % images.length);
  const prevImage = () => setActiveImageIndex(prev => (prev - 1 + images.length) % images.length);

  const getFieldType = (fieldId: string): string => {
    const field = fields.find(f => f.id === fieldId);
    return field?.type || 'text';
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 p-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white transition"
        >
          <X size={20} />
        </button>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left: Product Image & Details */}
          <div className="w-2/5 p-4 flex flex-col border-r border-gray-200 dark:border-gray-700">
            <div className="flex gap-2 mb-3 flex-shrink-0">
              {images.length > 1 && (
                <div className="flex flex-col gap-2 flex-shrink-0">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImageIndex(idx)}
                      className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition flex-shrink-0 ${
                        idx === activeImageIndex
                          ? 'border-blue-500 ring-2 ring-blue-400/50'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                      }`}
                    >
                      <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-contain bg-white p-0.5" />
                    </button>
                  ))}
                </div>
              )}
              <div className="relative flex-1 aspect-square bg-white rounded-lg overflow-hidden group">
                <img src={images[activeImageIndex]} alt={product.name} className="w-full h-full object-contain p-2" />
                {images.length > 1 && (
                  <>
                    <button onClick={prevImage} className="absolute left-1 top-1/2 -translate-y-1/2 p-1.5 bg-black/40 hover:bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition">
                      <ChevronLeft size={18} />
                    </button>
                    <button onClick={nextImage} className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 bg-black/40 hover:bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition">
                      <ChevronRight size={18} />
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-2">{product.name}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{product.description}</p>
            </div>
          </div>

          {/* Right: Customization Fields */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Action Button */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-center">
              <button
                onClick={handleAction}
                disabled={!isVariationSelected}
                className={`flex items-center gap-2 px-6 py-2.5 text-white font-semibold rounded-lg shadow-lg active:scale-95 transition ${
                  !isVariationSelected
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
                title={!isVariationSelected ? 'Please select a variation first' : undefined}
              >
                <Play size={18} />
                <span>Process Now</span>
              </button>
            </div>

            {/* Fields */}
            <div className="flex-1 p-4 overflow-y-auto">
              {loadingFields ? (
                <div className="text-gray-500 text-sm">Loading options...</div>
              ) : displayedFields.length === 0 ? (
                <div className="bg-amber-100 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800 rounded-lg p-3 flex items-center gap-2">
                  <AlertCircle className="text-amber-600 dark:text-amber-500 flex-shrink-0" size={16} />
                  <p className="text-sm text-amber-700 dark:text-amber-400">No customization options available.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {displayedFields.map((field) => {
                    const fieldType = getFieldType(field.id);
                    const isVariation = field.isVariationSelector;
                    const needsSelection = isVariation && !formValues[field.id];

                    return (
                      <div key={field.id} className="space-y-1">
                        <label className={`block text-xs font-medium ${needsSelection ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}`}>
                          {field.label}{needsSelection && ' *'}
                        </label>

                        {fieldType === 'select' && field.options && (
                          <select
                            value={formValues[field.id] || ''}
                            onChange={(e) => handleInputChange(field.id, e.target.value)}
                            className={`w-full p-2 text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-1 focus:ring-blue-500 outline-none ${
                              needsSelection ? 'border-amber-500 ring-2 ring-amber-300' : 'border-gray-300 dark:border-gray-600'
                            }`}
                          >
                            <option value="">{isVariation ? 'Select variation...' : 'Select...'}</option>
                            {field.options.map((opt, idx) => (
                              <option key={idx} value={opt}>{opt}</option>
                            ))}
                          </select>
                        )}

                        {fieldType === 'text' && (
                          <div className="space-y-1">
                            <input
                              type="text"
                              value={formValues[field.id] || ''}
                              onChange={(e) => handleInputChange(field.id, e.target.value)}
                              maxLength={field.maxLength}
                              placeholder={field.placeholder}
                              style={{ fontFamily: fieldFonts[field.id] || field.fontFamily }}
                              className="w-full p-2 text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-1 focus:ring-blue-500 outline-none border-gray-300 dark:border-gray-600"
                            />
                            {availableFonts[field.id] && availableFonts[field.id].length > 1 && (
                              <select
                                value={fieldFonts[field.id] || ''}
                                onChange={(e) => handleFontChange(field.id, e.target.value)}
                                className="w-full p-1.5 text-xs border rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600"
                              >
                                {availableFonts[field.id].map((f) => (
                                  <option key={f.id} value={f.family} style={{ fontFamily: f.family }}>
                                    {f.name}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        )}

                        {fieldType === 'checkbox' && (
                          <label className="flex items-center gap-2 cursor-pointer p-2">
                            <input
                              type="checkbox"
                              checked={formValues[field.id] === 'Yes'}
                              onChange={(e) => handleCheckboxChange(field.id, e.target.checked)}
                              className="w-4 h-4 rounded border-gray-400 text-blue-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Yes</span>
                          </label>
                        )}

                        {!['text', 'select', 'checkbox'].includes(fieldType) && (
                          <input
                            type="text"
                            value={formValues[field.id] || ''}
                            onChange={(e) => handleInputChange(field.id, e.target.value)}
                            placeholder={`Enter ${field.label}`}
                            className="w-full p-2 text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-1 focus:ring-blue-500 outline-none border-gray-300 dark:border-gray-600"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
